/**
 * NOVO CLIENTE e EDITAR CLIENTE — em passo a passo desde 17/09/2026.
 *
 * > *"'novo cliente' já não foi construído?"* — Richard, 17/09/2026. Estava, na
 * > janela única de 14/08; só o "Conferir e cadastrar" do link já tinha o
 * > desenho novo. Agora os dois são o mesmo passo a passo (`PassoAPasso.tsx`).
 *
 * ⛔ **Muda o desenho, não o que o botão faz.** Continua igual:
 *
 * - **Só o documento e o nome são obrigatórios.** Endereço e quem assina são o
 *   que o contrato precisa, mas exigir tudo de cara fazia do cadastro uma parede
 *   de campos que ninguém preenche — por isso os passos seguintes se pulam.
 * - **O CNPJ busca o cadastro da Receita e preenche SÓ O VAZIO**, e a tela diz
 *   o que preencheu (a barra verde). Consulta que sobrescreve apaga trabalho.
 * - **Documento repetido avisa antes de gravar**, e o servidor recusa de novo.
 * - **Gravar pergunta antes** ("Sim, salvar") e **fechar com alteração
 *   pendente avisa** (`lib/formulario.ts`).
 *
 * ⚠️ **No Editar, todos os passos abrem de uma vez e "Salvar" vale de qualquer
 * um** — quem edita veio mudar um campo, não refazer o cadastro.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { GuardaDoFormulario } from './GuardaDoFormulario.tsx';
import { Icone } from './Icone.tsx';
import {
  AjudaDoPasso,
  CampoDoPasso,
  PassosDoTopo,
  Progresso,
  ResumoDoPasso,
  UFS,
  mascaraCep,
  soDigitos,
  type MarcaDoCampo,
} from './PassoAPasso.tsx';
import { usarFormulario } from '../lib/formulario.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { maiuscula } from '../lib/caixa.ts';
import { apiClientes, type Cliente, type DadosCliente, type TipoPessoa } from '../lib/api.ts';
import { mascararDocumento } from '../lib/rotulos.ts';

type Props = {
  inicial: Cliente | null;
  /**
   * ⭐ **O cliente que NASCE preenchido**, vindo do funil (20/09/2026, PRD §49.9).
   *
   * ⚠️ **Não é "editando":** é um cadastro novo que começa com o que já se sabe.
   * Por isso a trilha continua sendo a de criar — passo a passo, com o documento
   * e o nome obrigatórios e a Receita preenchendo só o vazio.
   *
   * ⛔ **A razão social NÃO vem daqui.** O funil guarda o nome comercial, e esse
   * entra em *Nome fantasia*; `nome` fica vazio de propósito, para o CNPJ trazer
   * a razão social da Receita. Pôr o nome comercial ali faria contrato, proposta
   * e relatório saírem com o apelido no lugar do nome que a lei reconhece.
   */
  partida?: Partial<Campos> | null;
  /** Uma linha no alto do primeiro passo, dizendo de onde veio o que já está preenchido. */
  avisoDeAbertura?: ReactNode;
  aoFechar: () => void;
  aoSalvar: (dados: DadosCliente) => Promise<void>;
};

type Campos = {
  tipo: TipoPessoa;
  documento: string;
  nome: string;
  nomeFantasia: string;
  origem: string;
  site: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  email: string;
  telefone: string;
  representanteNome: string;
  representanteCpf: string;
  representanteCargo: string;
  observacao: string;
};
type Texto = Exclude<keyof Campos, 'tipo'>;

/** Os campos do cadastro, para quem abre o formulário já preenchido — hoje, o funil. */
export type CamposDoCliente = Campos;

const ULTIMO = 5;

function doCliente(c: Cliente | null): Campos {
  return {
    tipo: c?.tipo ?? 'pj',
    documento: c?.documentoFormatado ?? '',
    nome: c?.nome ?? '',
    nomeFantasia: c?.nomeFantasia ?? '',
    origem: c?.origem ?? '',
    site: c?.site ?? '',
    cep: c?.cep ?? '',
    logradouro: c?.logradouro ?? '',
    numero: c?.numero ?? '',
    complemento: c?.complemento ?? '',
    bairro: c?.bairro ?? '',
    cidade: c?.cidade ?? '',
    uf: c?.uf ?? '',
    email: c?.email ?? '',
    telefone: c?.telefone ?? '',
    representanteNome: c?.representanteNome ?? '',
    representanteCpf: c?.representanteCpf ? mascararDocumento(c.representanteCpf, 'pf') : '',
    representanteCargo: c?.representanteCargo ?? '',
    observacao: c?.observacao ?? '',
  };
}

export function FormularioCliente({ inicial, partida, avisoDeAbertura, aoFechar, aoSalvar }: Props) {
  const editando = inicial !== null;
  /* ⚠️ A partida só vale no cadastro NOVO: num cliente que já existe, sobrepor
     campo seria apagar o que está gravado — e nenhum botão desta casa faz isso. */
  const [d, setD] = useState<Campos>(() => (editando ? doCliente(inicial) : { ...doCliente(null), ...(partida ?? {}) }));
  const [passo, setPasso] = useState(0);
  const [alcancado, setAlcancado] = useState(editando ? ULTIMO : 0);
  const [erro, setErro] = useState<string | null>(null);
  const [repetido, setRepetido] = useState<string | null>(null);
  /** O que a Receita preencheu, com o valor que pôs: mexeu no campo, a barra verde sai. */
  const [daReceita, setDaReceita] = useState<Partial<Record<Texto, string>>>({});
  const [receita, setReceita] = useState<{ achou: boolean; situacao: string | null } | null>(null);
  const [salvando, setSalvando] = useState(false);
  const emVoo = useRef(false);

  const guarda = usarFormulario(d, aoFechar);
  /** O valor de agora, para a resposta da Receita decidir sem ler um fecho velho. */
  const atual = useRef(d);
  atual.current = d;
  usarDialogo(guarda.fechar);

  const pj = d.tipo === 'pj';
  const passos = [pj ? 'A empresa' : 'O cliente', 'Endereço', 'Contato', 'Quem assina', 'Para terminar', editando ? 'Conferir e salvar' : 'Conferir e gravar'];
  /** Mexeu num campo, o aviso de erro sai: aviso velho na tela manda corrigir o que já está certo. */
  const muda = (k: Texto, v: string) => {
    setErro(null);
    setD((x) => ({ ...x, [k]: v }));
  };

  /* Avisa do documento repetido ANTES de salvar — História 10. O servidor
     também recusa; isto poupa a pessoa de preencher o resto à toa. */
  useEffect(() => {
    const digitos = soDigitos(d.documento);
    const completo = d.tipo === 'pf' ? digitos.length === 11 : digitos.length === 14;
    if (!completo || digitos === inicial?.documento) return setRepetido(null);

    let valeu = true;
    apiClientes
      .conferirDocumento(digitos)
      .then((r) => {
        if (valeu) setRepetido(r.existe && r.cliente ? r.cliente.nome : null);
      })
      .catch(() => undefined);
    return () => {
      valeu = false;
    };
  }, [d.documento, d.tipo, inicial?.documento]);

  /*
   * ⭐ **A FICHA DA RECEITA, ao digitar o CNPJ — 07/09/2026.**
   *
   * ⚠️ **PREENCHE O QUE ESTÁ VAZIO, e nunca por cima.** Numa edição, portanto,
   * quase nada muda: só entra no campo que ainda está em branco.
   * ⚠️ **Só PJ.** Não há equivalente gratuito para CPF, e nome de pessoa física
   * custa dinheiro — gasto se autoriza por clique, nunca ao digitar.
   * ⚠️ **A tela DIZ o que preencheu** (barra verde e a lista na nota).
   */
  useEffect(() => {
    const digitos = soDigitos(d.documento);
    if (d.tipo !== 'pj' || digitos.length !== 14) {
      setReceita(null);
      setDaReceita({});
      return;
    }

    let valeu = true;
    apiClientes
      .pelocnpj(digitos)
      .then((r) => {
        if (!valeu) return;
        if (!r.achou || !r.empresa) return setReceita({ achou: false, situacao: null });
        const e = r.empresa;
        const trazido: Array<[Texto, string | null]> = [
          ['nome', e.nome],
          ['nomeFantasia', e.nomeFantasia],
          ['cep', e.cep ? mascaraCep(e.cep) : null],
          ['logradouro', e.logradouro],
          ['numero', e.numero],
          ['complemento', e.complemento],
          ['bairro', e.bairro],
          ['cidade', e.cidade],
          ['uf', e.uf],
        ];
        const postos: Partial<Record<Texto, string>> = {};
        for (const [k, v] of trazido) if (v && !atual.current[k].trim()) postos[k] = v;
        /* ⚠️ E o `setD` com função confere de novo: uma resposta atrasada não
           sobrescreve o que a pessoa acabou de digitar. */
        setD((x) => {
          const novo = { ...x };
          for (const [k, v] of Object.entries(postos) as Array<[Texto, string]>) if (!x[k].trim()) novo[k] = v;
          return novo;
        });
        setDaReceita(postos);
        setReceita({ achou: true, situacao: e.situacao });
      })
      .catch(() => undefined);
    return () => {
      valeu = false;
    };
  }, [d.documento, d.tipo]);

  const marca = (k: Texto): MarcaDoCampo => {
    if (k === 'documento') return receita?.achou ? 'lido' : undefined;
    const v = daReceita[k];
    return v !== undefined && d[k] === v ? 'lido' : undefined;
  };
  const preenchidos = (Object.keys(daReceita) as Texto[]).filter((k) => marca(k) === 'lido').length;

  const texto = (
    k: Texto,
    rotulo: string,
    col: 1 | 2 | 3 | 4 | 6,
    extra: { obrigatorio?: boolean; mono?: boolean; caixa?: boolean; nota?: string; max?: number; tipo?: string; mascara?: (v: string) => string; foco?: boolean } = {},
  ) => (
    <CampoDoPasso rotulo={rotulo} obrigatorio={extra.obrigatorio} col={col} mono={extra.mono} marca={marca(k)} nota={extra.nota}>
      <input
        type={extra.tipo ?? 'text'}
        value={d[k]}
        maxLength={extra.max ?? 200}
        autoFocus={extra.foco}
        inputMode={extra.mono && extra.mascara ? 'numeric' : undefined}
        onChange={(e) => {
          let v = e.target.value;
          if (extra.mascara) v = extra.mascara(v);
          else if (extra.caixa) v = maiuscula(v);
          muda(k, v);
        }}
      />
    </CampoDoPasso>
  );

  /** Só o passo 1 tem obrigatório — o resto se completa depois, na ficha. */
  function problema(): string | null {
    if (soDigitos(d.documento).length !== (pj ? 14 : 11)) return pj ? 'Informe o CNPJ (14 números).' : 'Informe o CPF (11 números).';
    if (!d.nome.trim()) return pj ? 'Escreva a razão social.' : 'Escreva o nome.';
    return null;
  }

  function irPara(p: number) {
    setErro(null);
    setPasso(p);
  }
  function avancar() {
    if (passo === 0) {
      const x = problema();
      if (x) return setErro(x);
      // Documento e nome em ordem: os demais passos ficam todos abertos.
      setAlcancado(ULTIMO);
    }
    irPara(passo + 1);
  }
  function pedirParaGravar() {
    if (emVoo.current) return;
    const x = problema();
    if (x) {
      setPasso(0);
      return setErro(x);
    }
    // A gravação só acontece depois do "sim". Ver lib/formulario.ts.
    guarda.pedirParaSalvar();
  }

  async function gravar() {
    if (emVoo.current) return;
    guarda.cancelarPendencia();
    emVoo.current = true;
    setSalvando(true);
    setErro(null);
    const ou = (v: string) => v.trim() || null;
    try {
      await aoSalvar({
        tipo: d.tipo,
        nome: d.nome.trim(),
        nomeFantasia: ou(d.nomeFantasia),
        documento: soDigitos(d.documento),
        email: ou(d.email),
        telefone: ou(d.telefone),
        origem: ou(d.origem),
        observacao: ou(d.observacao),
        site: ou(d.site),
        cep: ou(d.cep),
        logradouro: ou(d.logradouro),
        numero: ou(d.numero),
        complemento: ou(d.complemento),
        bairro: ou(d.bairro),
        cidade: ou(d.cidade),
        uf: d.uf.trim().toUpperCase() || null,
        representanteNome: ou(d.representanteNome),
        representanteCpf: soDigitos(d.representanteCpf) || null,
        representanteCargo: ou(d.representanteCargo),
      });
      guarda.marcarComoSalvo(d);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui salvar.');
      emVoo.current = false;
      setSalvando(false);
    }
  }

  const baixada = receita?.achou && receita.situacao && !/ativ/i.test(receita.situacao) ? receita.situacao : null;
  const ufs = d.uf && !UFS.includes(d.uf) ? [d.uf, ...UFS] : UFS;
  const ajuda = passo < ULTIMO ? AJUDA[passo] : undefined;

  return (
    <div className="cortina cad-cortina" onMouseDown={(e) => e.target === e.currentTarget && guarda.fechar()}>
      <div className="imv cad-conferir" role="dialog" aria-modal="true" aria-label={editando ? 'Editar cliente' : 'Novo cliente'}>
        <section className="imv-ficha">
          <header className="imv-ficha-topo">
            <div>
              <span className="s-sobrelinha">
                {editando ? 'Ficha do cliente · editar' : 'Carteira · novo cliente'} · passo {passo + 1} de {passos.length}
              </span>
              <h3 className="cad-titulo">{editando ? `Editar ${inicial?.nome ?? ''}` : 'Novo cliente'}</h3>
              <p className="cad-sub">
                {editando
                  ? 'Todos os passos estão abertos: mude o que precisar e salve de qualquer um deles.'
                  : `Só ${pj ? 'o CNPJ e a razão social são obrigatórios' : 'o CPF e o nome são obrigatórios'}. O resto pode ficar para depois, na ficha.`}
              </p>
            </div>
            <button type="button" className="imv-fechar" aria-label="Fechar" onClick={guarda.fechar}>
              <Icone nome="fechar" tamanho={16} />
            </button>
          </header>
          <PassosDoTopo passos={passos} atual={passo} alcancado={alcancado} aoIr={irPara} />

          <div className="imv-corpo">
            <div className="imv-conteudo">
              {preenchidos > 0 && (
                <p className="cad-legenda">
                  <span>
                    <i className="receita" /> preenchido pelo cadastro da Receita Federal — confira
                  </span>
                </p>
              )}

              {passo === 0 && (
                <>
                  {avisoDeAbertura && <div className="cad-mudou cad-vindo">{avisoDeAbertura}</div>}
                  <div className="imv-seg" style={{ marginBottom: 14 }}>
                    {(['pj', 'pf'] as const).map((t) => (
                      <button
                        key={t}
                        type="button"
                        className={`imv-op${d.tipo === t ? ' on' : ''}`}
                        onClick={() => {
                          if (d.tipo === t) return;
                          setErro(null);
                          setD((x) => ({ ...x, tipo: t, documento: '' }));
                        }}
                      >
                        <i />
                        {t === 'pj' ? 'Pessoa jurídica' : 'Pessoa física'}
                      </button>
                    ))}
                  </div>
                  <div className="imv-grade-campos">
                    {texto('documento', pj ? 'CNPJ' : 'CPF', 2, {
                      obrigatorio: true,
                      mono: true,
                      max: 18,
                      foco: !editando,
                      mascara: (v) => mascararDocumento(v, d.tipo),
                      nota: pj
                        ? receita?.achou
                          ? preenchidos > 0
                            ? `Encontrado na Receita Federal: ${preenchidos} campo${preenchidos === 1 ? '' : 's'} em branco preenchido${preenchidos === 1 ? '' : 's'}.`
                            : 'Encontrado na Receita Federal.'
                          : receita
                            ? 'Não foi possível buscar na Receita agora; siga digitando.'
                            : 'Ao completar, o Hub busca o cadastro da Receita Federal.'
                        : undefined,
                    })}
                    <span className="imv-campo c4" />
                    {texto('nome', pj ? 'Razão social' : 'Nome completo', 4, { obrigatorio: true, caixa: true })}
                    {pj ? texto('nomeFantasia', 'Nome fantasia', 2, { caixa: true }) : <span className="imv-campo c2" />}
                    {texto('origem', 'Quem indicou', 3, { caixa: true, nota: 'De onde veio este cliente.' })}
                    {texto('site', 'Site', 3)}
                  </div>
                  {(repetido || baixada) && (
                    <div className="cad-mudou">
                      {repetido && (
                        <span>
                          <b>Já existe um cliente com este documento:</b> {repetido}.
                        </span>
                      )}
                      {baixada && (
                        <span>
                          <b>A situação cadastral na Receita Federal é {baixada}.</b>
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}

              {passo === 1 && (
                <div className="imv-grade-campos">
                  {texto('cep', 'CEP', 2, { mono: true, max: 9, mascara: mascaraCep })}
                  <span className="imv-campo c4" />
                  {texto('logradouro', 'Logradouro', 4, { caixa: true })}
                  {texto('numero', 'Número', 1, { caixa: true, max: 20 })}
                  {texto('complemento', 'Complemento', 1, { caixa: true, max: 100 })}
                  {texto('bairro', 'Bairro', 2, { caixa: true, max: 100 })}
                  {texto('cidade', 'Cidade', 3, { caixa: true, max: 100 })}
                  <CampoDoPasso rotulo="UF" col={1} marca={marca('uf')}>
                    <select value={d.uf} onChange={(e) => muda('uf', e.target.value)}>
                      <option value="">—</option>
                      {ufs.map((u) => (
                        <option key={u}>{u}</option>
                      ))}
                    </select>
                  </CampoDoPasso>
                </div>
              )}

              {passo === 2 && (
                <>
                  <div className="imv-grade-campos">
                    {texto('email', 'E-mail', 3, { tipo: 'email' })}
                    {texto('telefone', 'Telefone', 2, { mono: true, max: 40 })}
                  </div>
                  <p className="cad-nada">
                    {editando ? 'As pessoas de contato ficam no quadro Contatos da ficha.' : 'As pessoas de contato (nome, cargo) se acrescentam na ficha, depois de gravar.'}
                  </p>
                </>
              )}

              {passo === 3 &&
                (pj ? (
                  <div className="imv-grade-campos">
                    {texto('representanteNome', 'Nome de quem assina', 3, { caixa: true })}
                    {texto('representanteCpf', 'CPF', 2, { mono: true, max: 14, mascara: (v) => mascararDocumento(v, 'pf') })}
                    <span className="imv-campo c1" />
                    {texto('representanteCargo', 'Cargo', 3, { caixa: true, max: 120 })}
                  </div>
                ) : (
                  <p className="cad-nada">Pessoa física: quem assina é o próprio cliente.</p>
                ))}

              {passo === 4 && (
                <div className="imv-grade-campos">
                  <CampoDoPasso rotulo="Observação" col={6}>
                    <textarea rows={4} value={d.observacao} maxLength={4000} onChange={(e) => muda('observacao', e.target.value)} />
                  </CampoDoPasso>
                </div>
              )}

              {passo === 5 && (
                <div className="cad-resumo">
                  <ResumoDoPasso titulo={pj ? 'A empresa' : 'O cliente'} aoCorrigir={() => irPara(0)} linhas={[[pj ? 'Razão social' : 'Nome', d.nome], ...(pj ? [['Nome fantasia', d.nomeFantasia] as [string, string]] : []), [pj ? 'CNPJ' : 'CPF', d.documento], ['Quem indicou', d.origem], ['Site', d.site]]} />
                  <ResumoDoPasso titulo="Endereço" aoCorrigir={() => irPara(1)} linhas={[['Logradouro', [d.logradouro, d.numero].filter(Boolean).join(', ') + (d.complemento ? ` — ${d.complemento}` : '')], ['Bairro', d.bairro], ['Cidade', d.cidade ? `${d.cidade}${d.uf ? `/${d.uf}` : ''}` : ''], ['CEP', d.cep]]} />
                  <ResumoDoPasso titulo="Contato" aoCorrigir={() => irPara(2)} linhas={[['E-mail', d.email], ['Telefone', d.telefone]]} />
                  <ResumoDoPasso titulo="Quem assina" aoCorrigir={() => irPara(3)} linhas={pj ? [['Nome', d.representanteNome], ['CPF', d.representanteCpf], ['Cargo', d.representanteCargo]] : [['Assina', d.nome]]} />
                  {d.observacao.trim() && (
                    <div className="cad-resumo-inteiro">
                      <ResumoDoPasso titulo="Para terminar" aoCorrigir={() => irPara(4)} linhas={[['Observação', d.observacao]]} />
                    </div>
                  )}
                </div>
              )}
            </div>
            {ajuda && <AjudaDoPasso titulo={ajuda.titulo}>{ajuda.corpo(pj, editando)}</AjudaDoPasso>}
          </div>

          {erro && <p className="erro imv-erro">{erro}</p>}

          <footer className="imv-ficha-pe">
            <button type="button" className="imv-bt fantasma" onClick={guarda.fechar}>
              Cancelar
            </button>
            <span className="imv-esp" />
            {passo > 0 && (
              <button type="button" className="imv-bt fantasma" onClick={() => irPara(passo - 1)}>
                <Icone nome="voltar" tamanho={14} /> Voltar
              </button>
            )}
            <Progresso atual={passo} total={passos.length} />
            {passo < ULTIMO && (
              <button type="button" className={`imv-bt ${editando ? 'fantasma' : 'primario'}`} onClick={avancar}>
                Avançar <Icone nome="avancar" tamanho={14} />
              </button>
            )}
            {(editando || passo === ULTIMO) && (
              <button type="button" className="imv-bt primario" disabled={salvando} onClick={pedirParaGravar}>
                <Icone nome="check" tamanho={14} /> {salvando ? 'Salvando…' : editando ? 'Salvar alterações' : 'Gravar cliente'}
              </button>
            )}
          </footer>
        </section>
      </div>

      <GuardaDoFormulario
        pendencia={guarda.pendencia}
        oQue="o cliente"
        aoCancelar={guarda.cancelarPendencia}
        aoSalvar={() => void gravar()}
        aoDescartar={guarda.aoFecharDeVerdade}
      />
    </div>
  );
}

/** A coluna da direita, passo a passo. Texto de tela: sem fonte, sem ferramenta. */
const AJUDA: Array<{ titulo: string; corpo: (pj: boolean, editando: boolean) => ReactNode }> = [
  {
    titulo: 'Como funciona',
    corpo: (pj, editando) => (
      <>
        <p>
          {pj ? (
            <>
              Ao completar o CNPJ, o Hub busca o cadastro da Receita Federal e preenche <b>só o que estiver em branco</b>. A barra <b>verde</b> marca o que veio de lá: confira.
            </>
          ) : (
            <>Para CPF não há busca automática: o nome se digita.</>
          )}
        </p>
        <p>{editando ? 'Os campos já preenchidos nunca são trocados pela busca.' : <>Obrigatórios são só os dois marcados. <b>Quem indicou</b> é o dado comercial mais valioso da casa.</>}</p>
      </>
    ),
  },
  {
    titulo: 'Endereço',
    corpo: (pj, editando) => (
      <p>
        É o endereço que sai no contrato.
        {editando
          ? pj
            ? ' A barra verde marca o que estava em branco e veio da Receita: confira antes de salvar.'
            : ''
          : `${pj ? ' Se o CNPJ foi encontrado, ele já veio da Receita.' : ''} Não tem agora? Avance: dá para completar na ficha.`}
      </p>
    ),
  },
  {
    titulo: 'Contato',
    corpo: () => <p>O e-mail e o telefone gerais do cliente. As pessoas com quem se fala entram na ficha, cada uma com nome e cargo.</p>,
  },
  {
    titulo: 'Quem assina',
    corpo: (pj) => <p>{pj ? 'Quem assina o contrato pela empresa. O contrato precisa de nome, CPF e cargo.' : 'Pessoa física assina por si: não há o que preencher aqui.'}</p>,
  },
  {
    titulo: 'Para terminar',
    corpo: () => <p>Anotação livre sobre o cliente. Fica só no Hub.</p>,
  },
];
